import type { PartnerDestination } from "@/types/partner-settings";

/** After a city change the activity list is replaced by a background sync. The selector waits this long before it takes another change. */
export const SYNC_WAIT_MS = 15_000;

/** Pause after the last keystroke before the search runs. */
export const SEARCH_DEBOUNCE_MS = 250;

export const normalizeDestinationQuery = (value: string): string =>
  value.trim().replace(/\s+/g, " ");

export const destinationLabel = (
  destination: Pick<PartnerDestination, "name" | "countryName">,
): string =>
  destination.countryName
    ? `${destination.name}, ${destination.countryName}`
    : destination.name;

export type DestinationSelectState =
  | "idle"
  | "loading"
  | "error"
  | "results"
  | "empty-can-search"
  | "remote-error"
  | "not-found";

interface StateInput {
  query: string;
  results: PartnerDestination[] | undefined;
  isLoading: boolean;
  isError: boolean;
  /** "City search" (provider) was started for this query. */
  remoteTried: boolean;
  remoteLoading: boolean;
  remoteError: boolean;
}

/**
 * What the dropdown shows. The provider button ("City search") appears only
 * when the cache has no match for the query.
 */
export const destinationSelectState = (
  input: StateInput,
): DestinationSelectState => {
  if (!normalizeDestinationQuery(input.query)) return "idle";
  if (input.isLoading || input.remoteLoading) return "loading";
  if (input.remoteTried && input.remoteError) return "remote-error";
  if (input.isError) return "error";
  if ((input.results?.length ?? 0) > 0) return "results";
  return input.remoteTried ? "not-found" : "empty-can-search";
};
